import { OrderEditableData, OrderModel } from 'lib/model/objects/orderModel';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { useState } from 'react';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { useNavigate } from 'react-router-dom';
import { ColumnService } from 'lib/services/directory';
import { useFirestoreDocumentListener } from 'lib/frontend/hooks/useFirestoreDocumentListener';
import { InvoiceModel } from 'lib/model/objects/invoiceModel';
import { OrderInvoice } from 'lib/types/invoices';
import { getFirebaseContext } from 'utils/firebase';
import { PRODUCT_TO_ROUTES } from 'router/routes';
import { getDynamicRoute } from 'lib/frontend/utils/router';
import { NewspaperOrderModel } from 'lib/model/objects/newspaperOrderModel';
import { sendCancelOrderRequest } from './actions/cancelOrder';
import MoreActions from './actions/MoreActions';
import { CancelOrderButton } from './actions/CancelOrderButton';
import { EditOrderButton } from './actions/EditOrderButton';

type OrderActionsProps = {
  order: OrderModel;
  editData: OrderEditableData | null;
  anonymousUserAuthInfo: { authEmail: string; accessCode: string } | undefined;
  newspaperOrders?: NewspaperOrderModel[];
  adTitle?: string;
};

export function OrderActions({
  order,
  editData,
  anonymousUserAuthInfo,
  newspaperOrders,
  adTitle
}: OrderActionsProps) {
  const navigate = useNavigate();
  const [invoiceIsUpdating, setInvoiceIsUpdating] = useState(false);

  const { isLoading: invoiceIsGetting, value: invoiceFromOrder } =
    useAsyncEffect<InvoiceModel<OrderInvoice> | null>({
      fetchData: async () => order.getInvoice(),
      dependencies: [order.id],
      errorConfig: {
        message: 'Error getting invoice on order details',
        service: ColumnService.DATABASE,
        tags: {
          orderId: order.id
        }
      }
    });

  const invoiceSnap = useFirestoreDocumentListener(invoiceFromOrder?.ref);
  const invoice = invoiceSnap
    ? new InvoiceModel<OrderInvoice>(getFirebaseContext(), {
        snap: invoiceSnap
      })
    : undefined;
  const invoiceIsLoading = invoiceIsGetting || invoiceIsUpdating;

  const handleSendToEditFlow = async (): Promise<ResponseOrError<void>> => {
    const { response, error: duplicateError } =
      await order.generateNewVersionOfOrderItems();
    if (duplicateError) {
      return wrapError(duplicateError);
    }

    const { version: newVersion } = response;
    navigate(
      getDynamicRoute(
        PRODUCT_TO_ROUTES[order.modelData.product].EDIT,
        {
          orderId: order.id
        },
        {
          version: newVersion.toString(),
          ...(anonymousUserAuthInfo
            ? {
                accessCode: anonymousUserAuthInfo.accessCode,
                email: anonymousUserAuthInfo.authEmail
              }
            : {})
        }
      )
    );
    return wrapSuccess(undefined);
  };

  const handleCancelOrder = async (
    cancellationReason: string
  ): Promise<ResponseOrError<void>> => {
    setInvoiceIsUpdating(true);
    const { error: cancelError } = await sendCancelOrderRequest(
      cancellationReason,
      order,
      anonymousUserAuthInfo
    );
    await invoice?.refreshData();
    setInvoiceIsUpdating(false);
    if (cancelError) {
      return wrapError(new Error('Failed to cancel order'));
    }
    return wrapSuccess(undefined);
  };

  return (
    <>
      <EditOrderButton
        editData={editData}
        onEditOrder={handleSendToEditFlow}
        disabled={false}
      />
      <CancelOrderButton order={order} onCancelOrder={handleCancelOrder} />
      <MoreActions
        order={order}
        invoice={invoice}
        invoiceIsLoading={invoiceIsLoading}
        newspaperOrders={newspaperOrders}
        adTitle={adTitle}
      />
    </>
  );
}
